export const techPartnersHero = {
  badge: "PIIC Technology Partnership",
  title: "Technology & Knowledge Partnership with PIIC",
  subtitle:
    "Partnership for Implementation of Innovation Circles (PIIC), an initiative of Textile Innovation Exchange (TIE) and Textile Today Innovation Hub (TTIH), is a factory-embedded innovation execution platform. Technology and knowledge partners play a strategic role in enabling factories to adopt practical solutions while strengthening their positioning in Bangladesh's evolving textile innovation ecosystem.",
  ctaPrimary: "Become a Technology Partner",
  ctaSecondary: "Learn More",
};

export const techPartnersWhatIs = {
  title: "What is PIIC?",
  paragraphs: [
    "Partnership for Implementation of Innovation Circles (PIIC) is designed to help textile and apparel factories institutionalize innovation through structured Innovation Circles, capability development, technology integration, and evidence-based performance improvement.",
    "As a prospective Technology & Knowledge Partner, your organisation can play a strategic role in enabling factories to adopt practical solutions while strengthening its own positioning in Bangladesh's evolving textile innovation ecosystem.",
  ],
};

export interface TechPartner {
  id: string;
  name: string;
  category: string;
  categoryKey: string;
  description: string;
  solutions: string[];
  verifiedFactories: string[];
  metrics: { label: string; value: string }[];
  partnerSince: string;
  featured?: boolean;
  logo?: string;
}

export const techPartnerCategories = [
  { key: "all", label: "All Partners", icon: "ri-apps-line" },
  { key: "dyeing", label: "Dyeing & Chemicals", icon: "ri-drop-line" },
  { key: "automation", label: "Automation & Robotics", icon: "ri-robot-2-line" },
  { key: "digital", label: "Digital Systems", icon: "ri-cpu-line" },
  { key: "sustainable", label: "Sustainable Materials", icon: "ri-leaf-line" },
  { key: "testing", label: "Testing & QC", icon: "ri-microscope-line" },
  { key: "energy", label: "Energy Solutions", icon: "ri-flashlight-line" },
  { key: "finishing", label: "Finishing Tech", icon: "ri-scissors-cut-line" },
];

export const techPartners: TechPartner[] = [
  {
    id: "dyepro",
    name: "DyePro Solutions",
    category: "Dyeing & Chemicals",
    categoryKey: "dyeing",
    description:
      "Specialists in low-liquor ratio dyeing systems and advanced chemical auxiliaries that reduce water consumption by up to 60% while maintaining color fastness standards.",
    solutions: [
      "Low-liquor ratio jet dyeing machines",
      "Bio-based dyeing auxiliaries",
      "Automated chemical dosing systems",
      "Shade matching AI software",
    ],
    verifiedFactories: ["APS Knit Composite", "Masco Group", "Micro Fibre Group"],
    metrics: [
      { label: "Water Reduction", value: "60%" },
      { label: "Chemical Savings", value: "35%" },
      { label: "RFT Improvement", value: "18%" },
    ],
    partnerSince: "2021",
    featured: true,
  },
  {
    id: "stitchbot",
    name: "StitchBot Automation",
    category: "Automation & Robotics",
    categoryKey: "automation",
    description:
      "Provider of collaborative sewing robots and automated material handling systems designed specifically for garment assembly lines in high-volume factories.",
    solutions: [
      "Collaborative sewing assist robots",
      "Automated fabric spreading systems",
      "AI-driven quality inspection stations",
      "Smart conveyor routing systems",
    ],
    verifiedFactories: ["Essential Clothing Ltd", "RH Corporation", "Salma Group"],
    metrics: [
      { label: "Throughput Gain", value: "22%" },
      { label: "Defect Reduction", value: "40%" },
      { label: "Labor Reallocation", value: "15%" },
    ],
    partnerSince: "2022",
    featured: true,
  },
  {
    id: "fabriq-digital",
    name: "FabriQ Digital",
    category: "Digital Systems",
    categoryKey: "digital",
    description:
      "Cloud-native manufacturing execution system (MES) with real-time production monitoring, OEE tracking, and predictive maintenance modules built for textile operations.",
    solutions: [
      "Real-time production monitoring dashboard",
      "OEE and productivity analytics",
      "Predictive maintenance alerts",
      "Order-to-delivery tracking system",
    ],
    verifiedFactories: ["AukoTex Group", "HAMS Group", "APS Knit Composite"],
    metrics: [
      { label: "OEE Improvement", value: "14%" },
      { label: "Downtime Reduction", value: "30%" },
      { label: "Lead-Time Cut", value: "12%" },
    ],
    partnerSince: "2020",
    featured: true,
  },
  {
    id: "greenfiber",
    name: "GreenFiber Labs",
    category: "Sustainable Materials",
    categoryKey: "sustainable",
    description:
      "Developer of recycled fiber blends, bio-based elastane alternatives, and sustainable yarn formulations that meet global certification standards.",
    solutions: [
      "Recycled cotton-polyester blends",
      "Bio-based stretch fiber alternatives",
      "Pre-consumer waste recycling systems",
      "Carbon footprint certified yarns",
    ],
    verifiedFactories: ["Micro Fibre Group", "Masco Group"],
    metrics: [
      { label: "Recycled Content", value: "40%" },
      { label: "Carbon Reduction", value: "25%" },
      { label: "Certifications", value: "GOTS / GRS" },
    ],
    partnerSince: "2021",
  },
  {
    id: "qualitech",
    name: "QualiTech Inspection Systems",
    category: "Testing & QC",
    categoryKey: "testing",
    description:
      "Automated fabric inspection and color measurement systems using machine vision and spectrophotometry for objective, repeatable quality control.",
    solutions: [
      "Machine vision fabric inspection",
      "Spectrophotometer color measurement",
      "Automated shrinkage testing",
      "Digital defect classification AI",
    ],
    verifiedFactories: ["Essential Clothing Ltd", "Salma Group", "AukoTex Group"],
    metrics: [
      { label: "Inspection Speed", value: "3x" },
      { label: "Color Accuracy", value: "99.2%" },
      { label: "Defect Detection", value: "95%" },
    ],
    partnerSince: "2022",
  },
  {
    id: "energysave-bd",
    name: "EnergySave Bangladesh",
    category: "Energy Solutions",
    categoryKey: "energy",
    description:
      "Industrial energy efficiency solutions focused on steam system optimization, compressed air recovery, and heat recycling for dyeing and finishing operations.",
    solutions: [
      "Steam trap monitoring & optimization",
      "Compressed air leak detection & recovery",
      "Heat exchanger retrofit systems",
      "Solar thermal integration for pre-heating",
    ],
    verifiedFactories: ["HAMS Group", "RH Corporation", "Micro Fibre Group"],
    metrics: [
      { label: "Energy Savings", value: "28%" },
      { label: "Steam Efficiency", value: "35%" },
      { label: "Payback Period", value: "14 mo" },
    ],
    partnerSince: "2020",
  },
  {
    id: "finishtech",
    name: "FinishTech Systems",
    category: "Finishing Tech",
    categoryKey: "finishing",
    description:
      "Advanced finishing equipment including ozone washing, laser denim finishing, and waterless softening systems that eliminate traditional wet processes.",
    solutions: [
      "Ozone-based garment washing",
      "Laser denim finishing systems",
      "Nano-bubble softening technology",
      "Automated pressing & folding lines",
    ],
    verifiedFactories: ["Masco Group", "APS Knit Composite"],
    metrics: [
      { label: "Water Elimination", value: "90%" },
      { label: "Chemical Reduction", value: "70%" },
      { label: "Process Time", value: "-45%" },
    ],
    partnerSince: "2023",
  },
  {
    id: "sewsmart",
    name: "SewSmart IoT",
    category: "Automation & Robotics",
    categoryKey: "automation",
    description:
      "IoT-enabled sewing machine monitoring platform that tracks needle position, thread tension, and motor load in real time to predict maintenance and optimize settings.",
    solutions: [
      "Sewing machine IoT sensor kits",
      "Real-time needle tracking dashboard",
      "Predictive needle replacement alerts",
      "Operator skill assessment modules",
    ],
    verifiedFactories: ["Essential Clothing Ltd", "Salma Group"],
    metrics: [
      { label: "Breakdown Reduction", value: "50%" },
      { label: "Needle Life Extension", value: "25%" },
      { label: "Operator Efficiency", value: "12%" },
    ],
    partnerSince: "2022",
  },
  {
    id: "chemcon",
    name: "ChemCon Bangladesh",
    category: "Dyeing & Chemicals",
    categoryKey: "dyeing",
    description:
      "Manufacturer of eco-friendly textile chemicals including bio-enzymes, natural dye fixatives, and low-temperature bleaching agents.",
    solutions: [
      "Bio-enzyme based desizing",
      "Low-temperature bleaching agents",
      "Natural dye fixatives",
      "pH-neutral softeners",
    ],
    verifiedFactories: ["AukoTex Group", "HAMS Group"],
    metrics: [
      { label: "Temperature Reduction", value: "30%" },
      { label: "Chemical Cost Cut", value: "20%" },
      { label: "Bio-Degradable", value: "95%" },
    ],
    partnerSince: "2021",
  },
  {
    id: "textile-ai",
    name: "TextileAI Analytics",
    category: "Digital Systems",
    categoryKey: "digital",
    description:
      "AI-powered demand forecasting and inventory optimization platform specifically trained on Bangladesh garment industry seasonal patterns and buyer behavior.",
    solutions: [
      "AI demand forecasting engine",
      "Raw material inventory optimizer",
      "Buyer order pattern analysis",
      "Automated reorder point calculator",
    ],
    verifiedFactories: ["Micro Fibre Group", "RH Corporation"],
    metrics: [
      { label: "Forecast Accuracy", value: "87%" },
      { label: "Inventory Reduction", value: "18%" },
      { label: "Stockout Reduction", value: "35%" },
    ],
    partnerSince: "2023",
  },
  {
    id: "ecowash",
    name: "EcoWash Technologies",
    category: "Finishing Tech",
    categoryKey: "finishing",
    description:
      "Water recycling and closed-loop washing systems that treat and reuse process water in dyeing and finishing operations with near-zero discharge.",
    solutions: [
      "Closed-loop water recycling systems",
      "Membrane filtration for process water",
      "Zero liquid discharge (ZLD) modules",
      "Real-time water quality monitoring",
    ],
    verifiedFactories: ["Masco Group", "APS Knit Composite", "Essential Clothing Ltd"],
    metrics: [
      { label: "Water Recovery", value: "85%" },
      { label: "Discharge Reduction", value: "92%" },
      { label: "Cost Savings", value: "30%" },
    ],
    partnerSince: "2020",
    featured: true,
  },
  {
    id: "powergrid-bd",
    name: "PowerGrid Bangladesh",
    category: "Energy Solutions",
    categoryKey: "energy",
    description:
      "On-site solar and biomass power generation solutions for textile factories, including rooftop solar, captive biomass boilers, and battery storage systems.",
    solutions: [
      "Rooftop solar PV systems",
      "Captive biomass boilers",
      "Battery energy storage integration",
      "Grid-tie net metering solutions",
    ],
    verifiedFactories: ["HAMS Group", "AukoTex Group", "Salma Group"],
    metrics: [
      { label: "Grid Dependency", value: "-40%" },
      { label: "Carbon Reduction", value: "35%" },
      { label: "Payback Period", value: "5.2 yr" },
    ],
    partnerSince: "2019",
  },
];

export const techPartnersVetting = {
  title: "The TIE Vetting Process",
  subtitle:
    "Every technology partner in the directory has passed a rigorous four-stage validation. TIE does not accept self-reported claims — every metric is verified through supervised factory pilots.",
  steps: [
    {
      phase: "Stage 1",
      title: "Application & Documentation",
      description:
        "The technology provider submits technical specifications, existing factory references, certifications, and performance claims. TIE reviews documentation for completeness and consistency.",
      icon: "ri-file-list-3-line",
    },
    {
      phase: "Stage 2",
      title: "Technical Due Diligence",
      description:
        "TIE's engineering cell conducts independent technical review — equipment audits, software code reviews, chemical composition analysis, or energy modeling validation as appropriate.",
      icon: "ri-search-line",
    },
    {
      phase: "Stage 3",
      title: "Supervised Factory Pilot",
      description:
        "The solution is deployed in a live TIE partner factory under PIIC or PTIM supervision. Baseline and endline metrics are collected independently by TIE programme directors.",
      icon: "ri-test-tube-line",
    },
    {
      phase: "Stage 4",
      title: "Ongoing Performance Monitoring",
      description:
        "Approved partners are re-evaluated annually. Factory feedback is collected quarterly. Partners with declining performance or unresolved complaints are suspended from the directory.",
      icon: "ri-shield-check-line",
    },
  ],
};

export const techPartnersEngagement = {
  title: "How Factories Engage With Partners",
  models: [
    {
      title: "Direct Matchmaking",
      description:
        "Factories describe their problem through TIE. The directory algorithm and programme directors recommend 2-3 vetted partners with verified solutions in that domain.",
      icon: "ri-user-search-line",
    },
    {
      title: "Supervised Pilot",
      description:
        "Factories can run a PTIM-style 10-week pilot with any directory partner. TIE supervises baseline, deployment, and validation. Go/no-go recommendation is independent.",
      icon: "ri-test-tube-line",
    },
    {
      title: "Expo Demonstration",
      description:
        "Partners demonstrate live at the Textile Innovation Expo. Factories see equipment in action, talk to other factory users, and book post-expo pilots at discounted rates.",
      icon: "ri-store-2-line",
    },
    {
      title: "Turn-Key Project",
      description:
        "For complex multi-system deployments, TIE coordinates the partner, factory, and internal experts as a turn-key project with defined deliverables and milestones.",
      icon: "ri-settings-3-line",
    },
  ],
};

export const techPartnersOutcomes = {
  title: "Verified Results From Partner Deployments",
  items: [
    {
      title: "Independently Verified Metrics",
      description:
        "Every performance claim on a partner profile has been measured by TIE programme directors during supervised pilots. No self-reported marketing numbers.",
      icon: "ri-shield-check-line",
    },
    {
      title: "Factory-Tested, Not Lab-Tested",
      description:
        "Solutions are validated in live production environments with real orders, real workers, and real constraints — not in ideal laboratory conditions.",
      icon: "ri-building-2-line",
    },
    {
      title: "Real Factory References",
      description:
        "Each partner profile lists the specific factories where their solution was deployed. Factories can contact references directly through TIE's introduction system.",
      icon: "ri-contacts-line",
    },
    {
      title: "Pilot-to-Scale Pathway",
      description:
        "Every engagement starts with a low-risk PTIM pilot. Factories only commit to full-scale deployment after independently validated results.",
      icon: "ri-arrow-up-circle-line",
    },
    {
      title: "Annual Re-Certification",
      description:
        "Partners must maintain performance standards and respond to factory feedback. The directory is dynamic — underperforming partners are removed.",
      icon: "ri-refresh-line",
    },
    {
      title: "Co-Marketing Through TIE",
      description:
        "Verified partners get featured in IJTAI case studies, AIRCon presentations, and Expo showcases. Independent validation becomes your strongest marketing asset.",
      icon: "ri-megaphone-line",
    },
  ],
};

export const techPartnersStats = [
  { value: "45+", label: "Vetted Partners", sub: "across 8 technology categories" },
  { value: "120+", label: "Factory Deployments", sub: "verified since 2019" },
  { value: "22%", label: "Average ROI", sub: "within first 12 months" },
  { value: "94%", label: "Factory Satisfaction", sub: "would recommend partner" },
];

export const techPartnersFactoryBenefits = {
  title: "Benefits for Textile & Apparel Factories",
  description:
    "PIIC delivers six structured benefits that help factories institutionalize innovation, develop their people, and achieve measurable, sustainable improvement.",
  items: [
    {
      title: "Structured Innovation System",
      description:
        "Factories receive a practical framework to identify operational problems through baseline analysis, find improvement options, validate outcomes with data, and institutionalize improvements through a systematic approach.",
      icon: "ri-stack-line",
    },
    {
      title: "Capability Development of Mid-Level Management",
      description:
        "PIIC develops factory professionals into problem-solvers and innovation leaders through hands-on implementation, training, and project-based peer learning.",
      icon: "ri-user-settings-line",
    },
    {
      title: "Evidence-Based Improvement",
      description:
        "Factories work with baseline and endline measurements, innovation model development, risk matrices, new innovation model integration with SOP, and implementation & scale-up within the factory.",
      icon: "ri-bar-chart-box-line",
    },
    {
      title: "Technology Integration Support",
      description:
        "Factories gain direct engagement with technology providers and industry experts to explore practical solutions in productivity, sustainability, process optimization, quality improvement, automation, and resource efficiency.",
      icon: "ri-cpu-line",
    },
    {
      title: "Cross-Factory Learning & Expert Engagement",
      description:
        "Factories participate in Innovation Cell sessions for peer learning, networking, technical discussions, and exposure to industry best practices.",
      icon: "ri-group-line",
    },
    {
      title: "Recognition & Industry Visibility",
      description:
        "Participating factories can showcase innovation outcomes at the Factory Pavilion during Textile Innovation Expo 2026 (12-14 Nov 2026 at ICCB), presenting real case studies, productivity gains, sustainability achievements, and innovation documentation.",
      icon: "ri-award-line",
    },
  ],
};

export const techPartnersPartnerBenefits = {
  title: "Benefits for Technology & Knowledge Partners",
  description:
    "Technology and knowledge partners gain structured pathways to demonstrate value, deepen client relationships, and build long-term positioning within Bangladesh's textile innovation ecosystem.",
  items: [
    {
      title: "Positioning as an Innovation Enabler",
      description:
        "PIIC allows technology partners to move beyond conventional sales engagement and position themselves as strategic innovation partners contributing to measurable factory transformation.",
      icon: "ri-lightbulb-line",
    },
    {
      title: "Practical Demonstration of Solutions",
      description:
        "Technology and solutions can be connected to real factory challenges, creating practical application opportunities and documented success stories.",
      icon: "ri-test-tube-line",
    },
    {
      title: "Stronger Client Relationships",
      description:
        "Through continuous interaction during implementation phases, Innovation Cell meetings, and technical discussions, partners can strengthen relationships with existing clients while opening opportunities with new factories.",
      icon: "ri-links-line",
    },
    {
      title: "Industry Branding & Visibility",
      description:
        "Partner recognition at PIIC Grand Launching, Grand Closing, Textile Innovation Expo, and PIIC communication materials creates strong visibility among industry stakeholders.",
      icon: "ri-megaphone-line",
    },
    {
      title: "Access to Innovation Ecosystem",
      description:
        "PIIC connects technology partners with factories, experts, academia, innovation leaders, and industry bodies through the wider TIE-TTIH ecosystem.",
      icon: "ri-earth-line",
    },
    {
      title: "Business Development Opportunity",
      description:
        "By engaging with real factory improvement initiatives, partners can identify emerging technology needs, support adoption, and generate long-term business opportunities.",
      icon: "ri-briefcase-line",
    },
  ],
};

export const techPartnersMission = {
  text: "Textile Innovation Exchange aims to transform Bangladesh's textile & apparel sector by embedding systematic, measurable, and sustainable innovation inside factories through PIIC. Through collaboration between factories, experts, and technology providers, the industry can build stronger competitiveness, operational excellence, and future readiness. Finally, we could claim us as an innovative industry globally.",
};